import React, { useState } from 'react';
import {
  Loader2,
  AlertCircle,
  CreditCard,
  CheckCircle2,
  Zap,
  Sparkles,
} from 'lucide-react';
import { REBILL_CHANGE_PLAN_URL } from '../config';
import { paymentServerFetch } from '../lib/paymentServer';

// Cambio de plan ONE-CLICK para clientes que YA están en Rebill (tienen tarjeta
// guardada). En vez del checkout embebido, muestra un botón "Confirmar cambio":
// el server cobra la tarjeta guardada (POST /rebill/change-plan, sin OTP) y el
// webhook `payment.created` completa la activación (créditos + baja de la sub
// vieja), igual que un pago del checkout.
//
// Respuestas del endpoint:
//  - { ok:true, subscriptionId } → cobrado → onSuccess.
//  - { ok:false, error:'needs_checkout' | 'no_saved_card' } → no se puede cobrar
//    en silencio → caemos al checkout embebido (onNeedsCheckout).
//  - { ok:false, error:'payment_declined' } → tarjeta rechazada → ofrecemos usar
//    otra tarjeta (→ checkout).
//  - { ok:false, error:'plan_not_in_rebill' } → plan sin mapear en el server →
//    error claro + fallback al checkout.

export interface RebillChangePlanResult {
  subscriptionId?: string;
}

interface RebillChangePlanConfirmProps {
  /** UUID del profile (auth user id) que espera el endpoint como `user_id`. */
  userId: string;
  /** Plan de Atlas destino: 'emprendimiento' | 'negocio' | 'enterprise' | ... */
  plan: string;
  /** Precio formateado para mostrar en el botón (opcional). */
  priceLabel?: string;
  /** Últimos 4 dígitos de la tarjeta guardada, si los tenemos (para que el user
   * sepa a qué tarjeta se le cobra). Opcional: si no viene, no se muestra. */
  cardLast4?: string | null;
  onSuccess: (result: RebillChangePlanResult) => void;
  /** El front debe pasar a mostrar el checkout embebido (cargar tarjeta). */
  onNeedsCheckout: (reason: string) => void;
}

type Status = 'idle' | 'loading' | 'success' | 'declined' | 'error';

const RebillChangePlanConfirm: React.FC<RebillChangePlanConfirmProps> = ({
  userId,
  plan,
  priceLabel,
  cardLast4,
  onSuccess,
  onNeedsCheckout,
}) => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Terminación de la tarjeta con la que se cobró (del response, si viene).
  const [chargedLast4, setChargedLast4] = useState<string | null>(null);

  const confirm = async () => {
    setStatus('loading');
    setErrorMsg(null);
    try {
      // paymentServerFetch inyecta el auth de Atlas + Content-Type solo.
      const res = await paymentServerFetch(REBILL_CHANGE_PLAN_URL, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, plan }),
      });
      const data = (await res.json().catch(() => null)) as
        | {
            ok?: boolean;
            subscriptionId?: string;
            error?: string;
            cardLastFour?: string;
            cardLast4?: string;
          }
        | null;

      if (data?.ok === true) {
        setChargedLast4(
          data.cardLastFour ?? data.cardLast4 ?? cardLast4 ?? null,
        );
        setStatus('success');
        onSuccess({ subscriptionId: data.subscriptionId });
        return;
      }
      const err = data?.error;
      // Sin tarjeta cobrable / no es cliente Rebill → cargar tarjeta en el checkout.
      if (err === 'needs_checkout' || err === 'no_saved_card') {
        onNeedsCheckout(err);
        return;
      }
      if (err === 'payment_declined') {
        setStatus('declined');
        return;
      }
      if (err === 'plan_not_in_rebill') {
        setStatus('error');
        setErrorMsg(
          'Este plan todavía no está disponible para el cambio directo.',
        );
        return;
      }
      setStatus('error');
      setErrorMsg('No pudimos procesar el cambio. Probá de nuevo.');
    } catch {
      setStatus('error');
      setErrorMsg('No pudimos conectar con el servidor. Probá de nuevo.');
    }
  };

  // Cambio confirmado → estado de éxito en el panel del form (el panel izquierdo
  // muestra la bienvenida). Evita que el botón quede en "Procesando…" post-cobro.
  if (status === 'success') {
    return (
      <div className="flex flex-col items-center text-center py-8">
        <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-brand-primary" />
        </div>
        <p className="text-[17px] font-semibold text-brand-black/90">
          ¡Cambio de plan confirmado!
        </p>
        {chargedLast4 && (
          <p className="text-[14px] text-neutral-500 mt-1.5">
            Cobrado a tu tarjeta terminada en •• {chargedLast4}
          </p>
        )}
      </div>
    );
  }

  // Tarjeta rechazada → el cliente carga otra vía el checkout embebido.
  if (status === 'declined') {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Tu tarjeta guardada fue rechazada. Probá con otra.</span>
        </div>
        <button
          type="button"
          onClick={() => onNeedsCheckout('payment_declined')}
          className="w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          Usar otra tarjeta
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header con ícono */}
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary/15 to-brand-primary/5 flex items-center justify-center mb-3">
          <Zap className="w-7 h-7 text-brand-primary" />
        </div>
        <h2 className="text-[19px] font-semibold text-brand-black/90 tracking-tight">
          Confirmá tu cambio de plan
        </h2>
        <p className="text-[14px] text-neutral-500 mt-1.5 leading-relaxed max-w-[320px]">
          Al confirmar, hacemos el cobro al instante y empezás a usar tu nuevo
          plan ya mismo.
        </p>
      </div>

      {/* Puntos clave */}
      <div className="rounded-2xl border border-neutral-200/80 divide-y divide-neutral-100 overflow-hidden">
        <div className="flex items-start gap-3 p-3.5">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-brand-black/90">
              Cobro inmediato
            </p>
            <p className="text-[13px] text-neutral-500 leading-snug">
              {cardLast4
                ? `Con tu tarjeta guardada terminada en •• ${cardLast4}, sin volver a cargarla.`
                : 'Con la tarjeta que ya tenés guardada, sin volver a cargarla.'}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3.5">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-brand-black/90">
              Acceso al instante
            </p>
            <p className="text-[13px] text-neutral-500 leading-snug">
              Ni bien confirmás, ya podés usar tu nuevo plan.
            </p>
          </div>
        </div>
      </div>

      {status === 'error' && errorMsg && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="button"
        onClick={confirm}
        disabled={status === 'loading'}
        className="w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Procesando…
          </>
        ) : (
          `Confirmar cambio de plan${priceLabel ? ` · ${priceLabel}` : ''}`
        )}
      </button>

      {status === 'error' && (
        <button
          type="button"
          onClick={() => onNeedsCheckout('error')}
          className="w-full text-[14px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
        >
          Pagar con otra tarjeta
        </button>
      )}
    </div>
  );
};

export default RebillChangePlanConfirm;
